import { parseJson } from '@/lib/common';
import React, { useCallback, useEffect, useState } from 'react';

export type UseLocalStorageStateType<V> = [
  V,
  React.Dispatch<React.SetStateAction<V>>,
];

const getCurrentLocalStorageValue = (storageKey: string, defaultValue: any) => {
  return parseJson(localStorage.getItem(storageKey), defaultValue);
};

const useLocalStorageState = <V>(
  defaultValue: V,
  storageKey: string,
): UseLocalStorageStateType<V> => {
  const [value, setValue] = useState<V>(() =>
    getCurrentLocalStorageValue(storageKey, defaultValue),
  );

  const setStorageValue = useCallback(
    (storedValue: V | ((preValue: V) => V)) => {
      const valueToStore =
        storedValue instanceof Function ? storedValue(value as V) : storedValue;
      setValue(valueToStore);
      localStorage.setItem(storageKey, JSON.stringify(valueToStore));
    },
    [storageKey, value],
  );

  useEffect(() => {
    const currentValue = getCurrentLocalStorageValue(storageKey, defaultValue);
    setValue(currentValue);
  }, [storageKey]); // eslint-disable-line react-hooks/exhaustive-deps

  return [value, setStorageValue];
};

export default useLocalStorageState;
